import DOMPurify from 'dompurify'
import type { DOMPurifyI } from 'dompurify'

const SVG_RE = /<svg ([^>]+)>/
const DATA_TYPE_ATTR = 'data-type'
const TYPE_UGC = 'ugc'

function queryAttribute(markup: string, attr: string) {
	const re = new RegExp(`${attr}=("|')?([-\\w\\s,]+)\\1`)
	return markup.match(re)
}

function getAttribute(markup: string, attr: string) {
	const attribute = queryAttribute(markup, attr)

	return attribute ? attribute[2] : null
}

function setAttribute(markup: string, attr: string, value: string) {
	const attribute = queryAttribute(markup, attr)

	if (attribute) {
		return markup.replace(attribute[2], value)
	}

	return markup.replace('<svg ', `<svg ${attr}="${value}" `)
}

class Sanitizer {
	private hasRealDOM: boolean
	purifier: DOMPurifyI

	constructor() {
		this.hasRealDOM = typeof window !== 'undefined'
		this.purifier = DOMPurify
	}
	/**
	 * Takes a string of SVG and sanitize it to be consumable by Santa.
	 *
	 * @param {string} svg
	 * @return {Object}
	 */
	sanitizeSVG(svg: string) {
		// allow href attributes that reference ID's inside the document
		this.purifier.addHook('afterSanitizeAttributes', (node: Element) => {
			if (
				node.hasAttribute('xlink:href') &&
				!node.getAttribute('xlink:href')!.match(/^#|^data:image|^data:img/)
			) {
				node.removeAttribute('xlink:href')
			}
		})

		// sanitize
		let cleaned = this.purifier.sanitize(svg, {
			RETURN_DOM_FRAGMENT: this.hasRealDOM, // remove once we upgrade JSDOM to +12 and get full SVG support
			KEEP_CONTENT: false,
			USE_PROFILES: {
				svg: true,
				svgFilters: true,
			},
			ADD_TAGS: ['use'],
		})

		// clean up
		this.purifier.removeHook('afterSanitizeAttributes')

		// get first <svg>
		const svgTag = this.hasRealDOM
			? (cleaned as DocumentFragment).querySelector('svg')
			: (cleaned as string).match(SVG_RE)?.[0]

		if (!svgTag) {
			// did not find an <svg>
			return {
				error: '',
			}
		}

		if (this.hasRealDOM) {
			// set data-type attribute
			// istanbul ignore next
			;(svgTag as SVGSVGElement).setAttribute(DATA_TYPE_ATTR, TYPE_UGC)

			// istanbul ignore next
			cleaned = (svgTag as SVGSVGElement).outerHTML
		} else {
			// set data-type attribute
			cleaned = (cleaned as string).replace(
				svgTag as string,
				setAttribute(svgTag as string, DATA_TYPE_ATTR, TYPE_UGC)
			)
		}

		const error = this.validateSVG(svgTag)

		if (error) {
			return {
				error,
			}
		}

		return {
			svg: cleaned,
		}
	}

	/**
	 * Validates an SVG follows Santa content restrictions and requirements.
	 *
	 * @param {SVGSVGElement|string} svg - SVG element or opening tag.
	 */
	validateSVG(svg: SVGSVGElement | string) {
		const isString = typeof svg === 'string'
		const width = isString ? getAttribute(svg as string, 'width') : (svg as SVGSVGElement).getAttribute('width')
		const height = isString ? getAttribute(svg as string, 'height') : (svg as SVGSVGElement).getAttribute('height')
		const viewBox = isString
			? getAttribute(svg as string, 'viewBox')
			: (svg as SVGSVGElement).getAttribute('viewBox')

		if (viewBox || (width && height)) {
			// we have at least viewBox OR (width AND height), no problem
			return null
		}

		return 'SVG does not have viewBox nor width and height'
	}
}

const sanitizer = new Sanitizer()

export { sanitizer }
